/* eslint-disable no-underscore-dangle */
/* eslint global-require: "off" */
/* eslint import/first: "off" */
// TODO: Disabled it for the time being
// import 'focus-visible';
import { DDPClient } from '@zedoc/ddp-client';
import { DDPConnector } from '@zedoc/ddp-connector';
import { compose, createStore, applyMiddleware } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'localforage';
import thunk from 'redux-thunk';
import { AwsRum } from 'aws-rum-web';
import { createRoot } from 'react-dom/client';
import { createBrowserHistory } from 'history';
import { routerMiddleware } from 'connected-react-router';
import { clearAllQuestionnaires } from '@zedoc/react-questionnaire';
import { decodeQuery } from '@zedoc/url';
import { resolveOnSelector } from '@zedoc/selectors';
import App from './containers/App';
import createReducer from './store/createReducer';
import { setDeferredPrompt } from './store/ui/drawer/actions';
import Loading from './common/components/Loading';
import { createMiddleware as createQuestionnaireMiddleware } from './store/questionnaire';
import resumeTokenKeepAlive from './store/resumeTokenKeepAlive';
import settings from './common/settings';
import { del } from './common/utilsClient/multiReducer';
import './common/logger/client/register';
import {
  cleanStorage,
  createAsyncStorage,
} from './common/utilsClient/redux/storage';
import transformMetaFields from './common/utilsClient/ddp/transformMetaFields';
import * as serviceWorker from './serviceWorker';
import './utils/i18next';
import './fonts';
import { tryLoginOnStartup } from './common/oauth/utils';
import './index.css';
import { realUserMonitoringMiddleware } from './store/realUserMonitoring';

// Set up AWS CloudWatch RUM - should happen as early as possible
let awsRum;
try {
  if (settings.public.awsCloudWatchRum) {
    const APPLICATION_ID = settings.public.awsCloudWatchRum.applicationId;
    const APPLICATION_VERSION = settings.public.awsCloudWatchRum.version;
    const APPLICATION_REGION = settings.public.awsCloudWatchRum.region;
    const IDENTITY_POOL_ID = settings.public.awsCloudWatchRum.identityPoolId;
    const RUM_ENDPOINT = settings.public.awsCloudWatchRum.endpoint;

    const config = {
      allowCookies: false,
      enableXRay: false,
      endpoint: RUM_ENDPOINT,
      identityPoolId: IDENTITY_POOL_ID,
      sessionSampleRate: 1,
      telemetries: ['performance'],
    };

    awsRum = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      config,
    );
  }
} catch (error) {
  // Ignore errors thrown during CloudWatch RUM web client initialization
  // as they should not block the application from starting
  console.error('Error initializing CloudWatch RUM web client:', error);
}

let isCookieLogin = false;

const ddpClient = new DDPClient({
  endpoint: settings.public.wsEndpoint,
  SocketConstructor: WebSocket,
  autoConnect: false, // we will connect manually once storage is defined
  reconnectInterval: 1000,
  handleLoginResume: function handleLoginResume() {
    const promise = tryLoginOnStartup(this);
    if (promise) {
      return promise;
    }

    isCookieLogin = true;
    return this.login({ cookieLogin: true }, { skipQueue: true }).catch(
      (err) => {
        isCookieLogin = false;
        console.warn(
          'Cookie login failed during resume, clearing session.',
          err,
        );
        this.emit('loginError', new Error('Cookie authentication failed'));
        return this.storage
          .del(`${this.endpoint}__login_token__`)
          .then(() => undefined);
      },
    );
  },
});

ddpClient.on('loggedIn', () => {
  if (isCookieLogin) {
    isCookieLogin = false;
    return;
  }

  ddpClient.storage
    .get(`${ddpClient.endpoint}__login_token__`)
    .then((token) => {
      if (!token) {
        return null;
      }
      return fetch(
        `${settings.public.providerUrl}/api/v1/identity/auth/set-cookie`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
          credentials: 'include',
        },
      ).then((res) => {
        if (res && res.ok) {
          try {
            localStorage.removeItem(`${ddpClient.endpoint}__login_token__`);
            localStorage.removeItem(
              `${ddpClient.endpoint}__login_token_expires__`,
            );
            localStorage.removeItem(`${ddpClient.endpoint}__user_id__`);
          } catch (e) {
            console.error('[Security] Failed to purge localStorage:', e);
          }
          return ddpClient.storage.del(`${ddpClient.endpoint}__login_token__`);
        }
        return null;
      });
    })
    .catch((err) => {
      console.error('Failed to set authentication cookie:', err);
    });
});

const ddpConnector = new DDPConnector({
  // debug: true,
  ddpClient,
  defaultLoaderComponent: Loading,
  transformRequest: transformMetaFields,
});

let enhancer;
const query = decodeQuery(window.location.search);

if (query.debug) {
  enhancer = compose(
    window.__REDUX_DEVTOOLS_EXTENSION__
      ? window.__REDUX_DEVTOOLS_EXTENSION__({})
      : (x) => x,
  );
} else if (process.env.NODE_ENV !== 'production') {
  enhancer = compose(
    window.__REDUX_DEVTOOLS_EXTENSION__
      ? window.__REDUX_DEVTOOLS_EXTENSION__({})
      : (x) => x,
  );
} else {
  enhancer = compose();
}

const history = createBrowserHistory();
const enhanceReducer = (reducer) =>
  persistReducer(
    {
      storage,
      key: 'zedoc',
      whitelist: ['storage', 'preferences'],
    },
    reducer,
  );

const middlewares = [
  routerMiddleware(history),
  thunk.withExtraArgument({
    ddpConnector,
  }),
  resumeTokenKeepAlive(),
  createQuestionnaireMiddleware(),
];
if (awsRum) {
  middlewares.push(realUserMonitoringMiddleware(awsRum));
}

const store = createStore(
  enhanceReducer(createReducer(history)),
  {},
  compose(applyMiddleware(...middlewares), enhancer),
);

const persistor = persistStore(store);
ddpClient.storage = createAsyncStorage(
  store,
  resolveOnSelector(persistor, 'bootstrapped'),
);
ddpClient.connect();

ddpClient.on('loggedOut', () => {
  store.dispatch(del('ui'));
  store.dispatch(cleanStorage());
  store.dispatch(clearAllQuestionnaires());
  fetch(`${settings.public.providerUrl}/api/v1/identity/auth/clear-cookie`, {
    method: 'POST',
    credentials: 'include',
  }).catch((err) => {
    console.error('Failed to clear authentication cookie:', err);
  });
});

ddpConnector.bindToStore(store);

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);
if (root) {
  root.render(
    <App
      awsRum={awsRum}
      store={store}
      history={history}
      persistor={persistor}
      ddpConnector={ddpConnector}
    />,
  );
}

if (process.env.NODE_ENV !== 'production') {
  if (typeof module !== 'undefined' && module.hot) {
    module.hot.accept('./containers/App', () => {
      const NextApp = require('./containers/App').default;
      if (root) {
        root.render(
          <NextApp
            store={store}
            history={history}
            persistor={persistor}
            ddpConnector={ddpConnector}
          />,
        );
      }
    });

    module.hot.accept('./store/createReducer', () =>
      store.replaceReducer(
        enhanceReducer(require('./store/createReducer').default(history)),
      ),
    );
  }
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();

  // eslint-disable-next-line no-console
  console.log('The app can be installed');

  store.dispatch(setDeferredPrompt(e));
});

window.addEventListener('appinstalled', () => {
  store.dispatch(setDeferredPrompt(null));
});

// Temporarily disable Service Worker to prevent caching of API / PW routes.
// We will revisit this in 3.1 with some form of opt-out / opt-in.
// serviceWorker.register({
//   onUpdate: (registration) => {
//     if (registration.waiting) {
//       registration.waiting.addEventListener('statechange', (event) => {
//         if (event.target.state === 'activated') {
//           window.location.reload();
//         }
//       });

//       registration.waiting.postMessage({
//         type: 'SKIP_WAITING',
//       });
//     }
//   },
// });

// Unregister Service Worker programatically for existing deployments.
serviceWorker.unregister();
